import url from '@/lib/api/url';
import Socket from '@/lib/socket/socket.js';

/**
 * 檢查該value是否於所屬Enum的value中
 * @param {Object} Enum 常數
 * @param {Any} value 常數的物件value
 * @returns {Boolean} 是否屬於
 */
const isValueExist = (Enum, value) => Object.values(Enum).includes(value);

const pvpSendEventEnum = {
  PVP_CREATE_LOBBY: 'createLobby', // 創建pvp等待室
  PVP_DELETE_LOBBY: 'deleteLobby', // 刪除pvp等待室
  PVP_ENTER_LOBBY: 'enterLobby', // 開始pvp對局
  PVP_SET_IS_READY: 'setIsReady', // 開始pvp對局
  PVP_REMOVE_OPPONENT: 'removeOpponent', // 踢掉對手
  PVP_LEAVE_LOBBY: 'leaveLobby', // 離開pvp對局
  PVP_START_GAME: 'startGame', // 離開pvp對局
  PVP_CREATE_AI_GAME: 'createAiGame', // 創建pvp AI對局
  PVP_MOVE: 'move', // pvp對局落子
  PVP_SEND_MESSAGE: 'sendMessage', // pvp對局發送訊息
  PVP_INVITE_JUDGEMENT: 'inviteJudgement', // pvp對局詢問是否算輸贏
  PVP_REFUSE_JUDGEMENT: 'refuseJudgement', // pvp對局拒絕算輸贏
  PVP_ACCEPT_JUDGEMENT: 'acceptJudgement', // pvp對局同意算輸贏
  PVP_REFUSE_JUDGEMENT_RESULT: 'refuseJudgementResult', // pvp對局拒絕算輸贏結果
  PVP_ACCEPT_JUDGEMENT_RESULT: 'acceptJudgementResult', // pvp對局接受算輸贏結果
  PVP_RESIGN: 'resign', // pvp對局投子
  PVP_INVITE_REMATCH: 'inviteRematch', // pvp對局要求重新對局
  PVP_ACCEPT_REMATCH: 'acceptRematch', // pvp對局同意重新對局
  PVP_REFUSE_REMATCH: 'refuseRematch', // pvp對局拒絕重新對局
  PVP_CHECK_OVERTIME: 'checkOvertime', // pvp對局檢查對手是否超時
  PVP_GET_UNFINISHED_GAME: 'getUnfinishedGame', // pvp對局查看未完成對局
  PVP_SPECTATE: 'spectate', // pvp對局觀戰
  PVP_DISCONNECT: 'disconnect', // pvp對局者斷線
};

// 後端event對應constant
const pvpResponseEventEnum = {
  PVP_CREATE_LOBBY_SUCCESSFULLY: 'createLobbySuccessfully', // 創建pvp等待室成功
  PVP_DELETE_LOBBY_SUCCESSFULLY: 'deleteLobbySuccessfully', // 刪除pvp等待室成功
  PVP_UPDATE_LOBBY_SUCCESSFULLY: 'updateLobbySuccessfully', // 更新pvp等待室成功
  PVP_ENTER_LOBBY_SUCCESSFULLY: 'enterLobbySuccessfully', // 加入pvp等待室成功
  PVP_LEAVE_LOBBY_SUCCESSFULLY: 'leaveLobbySuccessfully', // 離開pvp等待室成功
  PVP_GAME_STARTING: 'gameStarting', // 開始pvp對局
  PVP_GAME_STARTED: 'gameStarted', // 開始pvp對局
  PVP_MOVE: 'move', // pvp對局落子
  PVP_RECEIVE_MESSAGE: 'receiveMessage', // pvp對局訊息接收
  PVP_RECEIVE_JUDGEMENT_INVITATION: 'receiveJudgementInvitation', // pvp對局詢問是否算輸贏
  PVP_RECEIVE_JUDGEMENT_REFUSAL: 'receiveJudgementRefusal', // pvp對局拒絕算輸贏
  PVP_RECEIVE_JUDGEMENT_CONFIRMATION: 'receiveJudgementConfirmation', // pvp對局接受算輸贏
  PVP_RECEIVE_JUDGEMENT_RESULT_REFUSAL: 'receiveJudgementResultRefusal', // pvp對局拒絕算輸贏結果
  PVP_RECEIVE_JUDGEMENT_RESULT_CONFIRMATION:
    'receiveJudgementResultConfirmation', // pvp對局接受算輸贏結果
  PVP_GAME_UNJUDGEABLE: 'gameUnjudgeable', // pvp對局棋局尚未結束，無法算輸贏
  PVP_GAME_ENDED: 'gameEnded', // pvp對局結束
  PVP_RECEIVE_REMATCH_INVITATION: 'receiveRematchInvitation', // pvp再來一局請求
  PVP_RECEIVE_REMATCH_CONFIRMATION: 'receiveRematchConfirmation', // pvp對局重新對局請求
  PVP_RECEIVE_REMATCH_REFUSAL: 'receiveRematchRefusal', // pvp對局拒絕重新對局
  PVP_EXIST_PVP_GAME: 'existPvpGame', // pvp對局未完成對局
  PVP_DISCONNECT: 'disconnect',
  PVP_SPECTATE_SUCCESSFULLY: 'spectateSuccessfully', // pvp對局觀戰清單
  PVP_SPECTATE: 'spectate', // pvp對局使用者進入觀戰
  PVP_SPECTATOR_ENTERED: 'spectatorEntered', // pvp對局觀戰者進入房間
};

class AuthSocket extends Socket {
  constructor() {
    super();
    this.jwt = null;
    this.socketUrl = url.socket;
    this.addSendEventEnum(pvpSendEventEnum);
    this.addResponseEventEnum(pvpResponseEventEnum);
  }

  init(jwt, router) {
    this.jwt = jwt;
    super.init(router);
  }

  clearPvpEvents() {
    const responseEvents = Object.values(pvpResponseEventEnum);
    const sendEvents = Object.values(pvpSendEventEnum);
    const pvpEvents = [...responseEvents, ...sendEvents];
    this.clearEvents(pvpEvents);
  }

  send(payload, isForce) {
    const payloadObject =
      typeof payload === 'string' ? JSON.parse(payload) : payload;
    if (!payloadObject.jwt) {
      payloadObject.jwt = this.jwt;
    }
    super.send(payloadObject, isForce);
  }

  socketMessageCallback(event, isWaitingRun) {
    const data = JSON.parse(event.data);
    const {pvpAction, action} = data;
    if (
      !isWaitingRun &&
      this.waitingActionNames?.includes(pvpAction || action)
    ) {
      return this.waitingEvents.push(event);
    }

    switch (data.action) {
      case 'pvp':
        if (isValueExist(this.ResponseEventEnum, data.pvpAction)) {
          this.cbMap[data.pvpAction].forEach((cb) => cb(data));
          this.execRouteCb(data.pvpAction, data);
          this.onceCbMap[data.pvpAction].forEach((cb) => cb(data));
          this.onceCbMap[data.pvpAction] = [];
        } else {
          throw new Error('Unknown event from server');
        }
        break;
      default:
        super.socketMessageCallback(event, isWaitingRun);
    }
  }
}

export default new AuthSocket();
